import React, { useState, useEffect, useRef, useCallback } from "react";
import { API_BASE_URL, MONTHS, formatDateOnly } from "./constants/adminConstants";
import AdminMyChanges from "./AdminMyChanges";
import "./css/adminStyles.css";

function AdminDashboard({ user, onNavigate }) {
  const [stats, setStats] = useState({ pendingUsers: 0, totalUsers: 0, recentLogins: 0, pendingChanges: 0 });
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [showHistoryDash, setShowHistoryDash] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const rolePath = user?.role?.toLowerCase() || "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchNotifications = useCallback(async () => {
    const token = sessionStorage.getItem("tvc_token");
    if (!token) return;

    try {
      let endpoint;
      if (user.role === "Admin") endpoint = `${API_BASE_URL}/admin/notifications`;
      else if (user.role === "Manager") endpoint = `${API_BASE_URL}/manager/notifications`;
      else endpoint = `${API_BASE_URL}/staff/notifications`;

      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();

      if (res.ok) {
        setNotifications(data.notifications || []);
        setFilteredNotifications(data.notifications || []);
      }
    } catch (err) {
      console.error("Fetch notifications error:", err);
    } finally {
      setNotifLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    let filtered = notifications;
    if (showHistoryDash) {
      filtered = filtered.filter((n) => n.read);
    } else {
      filtered = filtered.filter((n) => !n.read);
    }

    if (monthFilter) {
      filtered = filtered.filter((n) => {
        const d = n.createdAt ? new Date(n.createdAt) : null;
        return d && d.getMonth() === parseInt(monthFilter);
      });
    }

    if (yearFilter) {
      filtered = filtered.filter((n) => {
        const d = n.createdAt ? new Date(n.createdAt) : null;
        return d && d.getFullYear() === parseInt(yearFilter);
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (n) =>
          (n.title || "").toLowerCase().includes(q) ||
          (n.message || "").toLowerCase().includes(q)
      );
    }

    setFilteredNotifications(filtered);
  }, [monthFilter, yearFilter, searchQuery, notifications, showHistoryDash]);

  const fetchData = async () => {
    const token = sessionStorage.getItem("tvc_token");
    if (!token) return;

    try {
      if (user.role === "Admin") {
        const [pendingRes, usersRes, historyRes, changesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/pending-users`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/admin/all-users`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/admin/login-history`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/admin/change-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const pending = await pendingRes.json();
        const users = await usersRes.json();
        const history = await historyRes.json();
        const changes = await changesRes.json();

        setStats({
          pendingUsers: pending.users?.length || 0,
          totalUsers: users.users?.length || 0,
          recentLogins: history.history?.length || 0,
          pendingChanges: changes.changeRequests?.filter(c => c.status === "manager_approved")?.length || 0,
        });
      } else if (user.role === "Manager") {
        const [changesRes, historyRes] = await Promise.all([
          fetch(`${API_BASE_URL}/manager/pending-changes`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/manager/login-history`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const changes = await changesRes.json();
        const history = await historyRes.json();

        setStats({
          pendingChanges: changes.changeRequests?.length || 0,
          recentLogins: history.history?.length || 0,
          pendingUsers: 0,
          totalUsers: 0,
        });
      } else {
        const [changesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/staff/my-changes`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const changes = await changesRes.json();
        setStats({
          pendingChanges: changes.changeRequests?.filter(c => c.status === "pending_manager")?.length || 0,
          recentLogins: 0,
          pendingUsers: 0,
          totalUsers: 0,
        });
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
  };

  const handleMarkRead = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      let endpoint;
      if (user.role === "Admin") endpoint = `${API_BASE_URL}/admin/notifications/${id}/read`;
      else if (user.role === "Manager") endpoint = `${API_BASE_URL}/manager/notifications/${id}/read`;
      else endpoint = `${API_BASE_URL}/staff/notifications/${id}/read`;

      await fetch(endpoint, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: 1 } : n))
      );
    } catch (err) {
      console.error("Mark read error:", err);
    }
  };

  const handleDeleteNotification = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      let endpoint;
      if (user.role === "Admin") endpoint = `${API_BASE_URL}/admin/notifications/${id}`;
      else if (user.role === "Manager") endpoint = `${API_BASE_URL}/manager/notifications/${id}`;
      else endpoint = `${API_BASE_URL}/staff/notifications/${id}`;

      const res = await fetch(endpoint, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        setDeleteTarget(null);
      }
    } catch (err) {
      console.error("Delete notification error:", err);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);

  return (
    <div>
      <div className="AdminGrid3">
        {user.role === "Admin" && (
          <>
            <div className="AdminStatCard" style={{ cursor: "pointer" }} onClick={() => onNavigate("/admin/user-approvals")}>
              <p className="AdminStatValue">{stats.pendingUsers}</p>
              <p className="AdminStatLabel">Pending Approvals</p>
            </div>
            <div className="AdminStatCard">
              <p className="AdminStatValue">{stats.totalUsers}</p>
              <p className="AdminStatLabel">Total Users</p>
            </div>
            <div className="AdminStatCard" style={{ cursor: "pointer" }} onClick={() => onNavigate("/admin/change-requests")}>
              <p className="AdminStatValue">{stats.pendingChanges}</p>
              <p className="AdminStatLabel">Pending Admin Approvals</p>
            </div>
          </>
        )}
        {user.role === "Manager" && (
          <>
            <div className="AdminStatCard" style={{ cursor: "pointer" }} onClick={() => onNavigate("/manager/staff-changes")}>
              <p className="AdminStatValue">{stats.pendingChanges}</p>
              <p className="AdminStatLabel">Pending Staff Changes</p>
            </div>
            <div className="AdminStatCard">
              <p className="AdminStatValue">{stats.recentLogins}</p>
              <p className="AdminStatLabel">Login Records</p>
            </div>
            <div className="AdminStatCard">
              <p className="AdminStatValue">{user.firstName}</p>
              <p className="AdminStatLabel">Logged in as Manager</p>
            </div>
          </>
        )}
        {user.role === "Staff" && (
          <>
            <div className="AdminStatCard" style={{ cursor: "pointer" }} onClick={() => onNavigate("/staff/my-changes")}>
              <p className="AdminStatValue">{stats.pendingChanges}</p>
              <p className="AdminStatLabel">Pending Changes</p>
            </div>
            <div className="AdminStatCard" style={{ cursor: "pointer" }} onClick={() => onNavigate("/staff/submit-change")}>
              <p className="AdminStatValue">+</p>
              <p className="AdminStatLabel">Submit New Change</p>
            </div>
          </>
        )}
      </div>

      {user.role === "Staff" && (
        <div style={{ marginTop: 24 }}>
          <AdminMyChanges />
        </div>
      )}

      <div className="AdminCard" style={{ marginTop: 24 }}>
        <div className="AdminCardHeader">
           <h3 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 700, color: "#1B2A4A" }}>Notifications <span className="AdminCountBadge">{filteredNotifications.length}</span></h3>
          <div className="AdminPillFilterBar">
            <div className="AdminPillGroup">
              <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search title, message..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                   onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
                />
                {!searchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
            </div>

            <div className="AdminPillGroup">
              <div className="AdminPillFilterItem">
                <button
                  className="AdminPillSelect"
                  style={{ cursor: "pointer", minWidth: 100, textAlign: "center" }}
                  onClick={() => setShowHistoryDash((prev) => !prev)}
                >
                  {showHistoryDash ? "Show Unread" : "History"}
                </button>
              </div>
              <div className="AdminPillFilterItem">
                <select className="AdminPillSelect" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                  <option value="">Months</option>
                  {MONTHS.map((m, i) => (
                    <option key={i} value={i}>{m}</option>
                  ))}
                </select>
                {monthFilter && (
                  <button className="AdminPillClear" onClick={() => setMonthFilter("")}>&times;</button>
                )}
              </div>

              <div className="AdminPillFilterItem">
                <select className="AdminPillSelect" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                  <option value="">Years</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {yearFilter && (
                  <button className="AdminPillClear" onClick={() => setYearFilter("")}>&times;</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {notifLoading ? (
          <div className="AdminEmptyState">Loading notifications...</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="AdminEmptyState">No notifications found.</div>
        ) : (
          <div className="AdminTableScroll">
          <table className="AdminTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Message</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotifications.map((n) => (
                <tr key={n.id}>
                  <td>{formatDateOnly(n.createdAt)}</td>
                  <td style={{ fontWeight: n.read ? 400 : 600 }}>
                    {n.title}{n.actorId === user.id ? <span className="AdminMeTag"> (Me)</span> : null}
                  </td>
                  <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#64748b" }}>
                    {n.message || "-"}
                  </td>
                  <td>
                    <span className={`AdminBadge ${n.read ? "AdminBadgeActive" : "AdminBadgePending"}`}>
                      {n.read ? "Read" : "Unread"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {!n.read && (
                        <button
                          className="AdminButton AdminButtonSmall AdminButtonSuccess"
                          onClick={() => handleMarkRead(n.id)}
                        >
                          Mark Read
                        </button>
                      )}
                      <button
                        className="AdminButton AdminButtonSmall"
                        style={{ background: "#dc2626" }}
                        onClick={() => setDeleteTarget(n)}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {deleteTarget && (
          <div className="AdminOverlay" onClick={() => setDeleteTarget(null)}>
            <div className="AdminOverlayCard" onClick={(e) => e.stopPropagation()}>
              <div className="AdminOverlayHeader">
                <h3>Delete Notification</h3>
                <button className="AdminIconBtn" onClick={() => setDeleteTarget(null)} title="Close" style={{fontSize:28,width:36,height:36}}>&times;</button>
              </div>
              <div style={{padding:20,textAlign:"center"}}>
                <p style={{color:"#64748b",fontSize:15,margin:"0 0 20px",fontFamily:"'DM Sans',sans-serif"}}>
                  Are you sure you want to permanently delete this notification?
                </p>
                <div style={{marginBottom:12,textAlign:"left",background:"#f8fafc",padding:12,borderRadius:8}}>
                  <p style={{margin:"0 0 4px",fontWeight:600,fontSize:14}}>{deleteTarget.title}</p>
                  {deleteTarget.message && (
                    <p style={{margin:0,fontSize:13,color:"#64748b"}}>{deleteTarget.message}</p>
                  )}
                </div>
                <div className="AdminFlexRowGap8" style={{justifyContent:"center"}}>
                  <button className="AdminButton AdminButtonSecondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
                  <button className="AdminButton" style={{background:"#dc2626"}} onClick={() => handleDeleteNotification(deleteTarget.id)}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
